// INV27-F3-S2: one exact final quarter-dollar rounding for order-unit cost.
// Browser-safe. Prices use package volume, not an assumed single bottle.
import { parseQuantity } from "./inventoryQuantity.ts";

const SCALE = 10_000n;

export function markupBasisPoints(percent: number): bigint {
  const text = String(percent);
  if (!Number.isFinite(percent) || !/^\d+(\.\d{1,2})?$/.test(text)) {
    throw new RangeError(
      "Target markup must be a non-negative percentage with at most two decimals.",
    );
  }
  const [whole, fraction = ""] = text.split(".");
  return BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, "0"));
}

function calculatedPrice(
  costCents: number,
  unitsNumerator: bigint,
  unitsDenominator: bigint,
  markup: number,
): number {
  if (!Number.isSafeInteger(costCents) || costCents < 0 || unitsNumerator <= 0n) {
    throw new RangeError("A valid cost and package size are required.");
  }
  const numerator = BigInt(costCents) * unitsDenominator * (10_000n + markupBasisPoints(markup));
  const denominator = unitsNumerator * 10_000n * 25n;
  const cents = ((numerator + denominator - 1n) / denominator) * 25n;
  if (cents > 2_147_483_647n) throw new RangeError("The calculated price is too large.");
  return Number(cents);
}

export function calculatedUnitPriceCents(
  costCents: number,
  bottlesPerOrderUnit: string,
  markup: number,
): number {
  return calculatedPrice(
    costCents,
    parseQuantity(bottlesPerOrderUnit, "package size"),
    SCALE,
    markup,
  );
}

export function calculatedPerOuncePriceCents(params: {
  costCents: number;
  bottlesPerOrderUnit: string;
  /** Ounces in one whole container: a keg's keg size volume, otherwise the
   * item's Size (KEGMATH1; resolveOuncesPerContainer). Null withholds the price. */
  ouncesPerContainer: string | null;
  targetMarkupPercent: number;
}): number | null {
  const ounces = params.ouncesPerContainer;
  if (ounces === null) return null;
  return calculatedPrice(
    params.costCents,
    parseQuantity(params.bottlesPerOrderUnit, "package size") * parseQuantity(ounces, "size"),
    SCALE * SCALE,
    params.targetMarkupPercent,
  );
}
